import { CssBaseline, ThemeProvider } from '@mui/material';
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { createAppTheme } from './createAppTheme';
import { DEFAULT_THEME_MODE, THEME_MODE_STORAGE_KEY, type ThemeMode } from './ThemeMode';
import { ThemeModeContext, type ThemeModeState } from './ThemeModeContext';

const readStoredMode = (): ThemeMode => {
  try {
    const stored = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);

    return stored === 'light' || stored === 'dark' ? stored : DEFAULT_THEME_MODE;
  } catch {
    // A browser with site data blocked simply gets the default on every load.
    return DEFAULT_THEME_MODE;
  }
};

export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);

  const toggle = useCallback((): void => {
    setMode((current) => {
      const next: ThemeMode = current === 'dark' ? 'light' : 'dark';

      try {
        window.localStorage.setItem(THEME_MODE_STORAGE_KEY, next);
      } catch {
        // The choice simply does not survive a reload.
      }

      return next;
    });
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo<ThemeModeState>(() => ({ mode, toggle }), [mode, toggle]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
